import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  getDefaultCanvasSize,
  getEditorDesignSize,
  getEditorObjects,
  getEditorSelectedId,
  getFonts,
  getResolvedBackgroundDetailImageUrl,
  getResolvedBackgroundOption,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import { removeEditorItemAndSync, setEditorSelectedId, updateEditorItem } from '../../Reducers/Configurator/Actions';
import { AttributeIdentifiers, BackgroundTypes } from '../../Enums';
import { XXL_COLORS } from '../../theme/colors';
import BackgroundLayer from './BackgroundLayer';
import DesignItemLayer, { DesignItemHitLayer } from './DesignItemLayer';
import ItemMeasurements from './ItemMeasurements';
import SelectionOverlay from './SelectionOverlay';
import SurfaceGlossFilter from './SurfaceGlossFilter';
import SurfaceMatteFilter from './SurfaceMatteFilter';
import { rectFitCanvas, useEditorViewport } from './useEditorViewport';
import { useItemStrokeWidths } from './useItemStrokeWidths';
import { computeDesignSize } from '../../utils/designSize';
import {
  DEFAULT_CANVAS_HEIGHT_MM,
  DEFAULT_CANVAS_WIDTH_MM,
} from '../../utils/editorUnits';
import {
  computeFilterBlurRadius,
  isGlossSurface,
  isMatteSurface,
  SURFACE_GLOSS_FILTER_ID,
  SURFACE_GLOSS_GRADIENT_ID,
  SURFACE_GLOSS_ITEMS_ID,
  SURFACE_GLOSS_MASK_ID,
  SURFACE_GLOSS_STREAK_GRADIENT_ID,
  SURFACE_GLOSS_WARP_FILTER_ID,
  SURFACE_MATTE_FILTER_ID,
  SURFACE_MATTE_GRADIENT_ID,
  SURFACE_MATTE_ITEMS_ID,
  SURFACE_MATTE_MASK_ID,
  SURFACE_MATTE_WARP_FILTER_ID,
  SURFACE_MATTE_STREAK_GRADIENT_ID,
} from '../../utils/surfaceFilter';
import type { EditorItem } from '../../Reducers/Configurator/types';

const DRAG_THRESHOLD_PX = 4;
const SURFACE_ID = OptionClassificationIdentifiers.surface;

/** Pointer events inside the design layer only ever target the transparent
 *  hit rects (everything else is pointer-events: none). */
function shouldDelegateToEditorItem(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[data-editor-item]') !== null;
}

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: { canvasBackground?: string } }).xxlprint;

  return {
    root: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: tokens?.canvasBackground || '#f6f6f6',
      overflow: 'hidden',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    },
    svg: {
      width: '100%',
      height: '100%',
      display: 'block',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    },
    // Screen-space loading indicator for the background image — centered on the
    // visible canvas (not in the SVG's zoom/pan world coordinates).
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    },
    '@keyframes canvasSpin': {
      to: { transform: 'rotate(360deg)' },
    },
    spinner: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      border: `3px solid ${XXL_COLORS.borderDefault}`,
      borderTopColor: XXL_COLORS.borderStrong,
      animation: '$canvasSpin 0.8s linear infinite',
    },
  };
});

type PanState = {
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  moved: boolean;
};

export default function SvgDesignCanvas() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [viewport, setViewport] = React.useState({ width: 0, height: 0 });
  const [backgroundLoading, setBackgroundLoading] = React.useState(false);
  // Delay the spinner so fast/cached background loads don't flash it (which reads as
  // "a spinner appeared then nothing happened"). Only show it for genuinely slow loads.
  const [showBackgroundSpinner, setShowBackgroundSpinner] = React.useState(false);
  React.useEffect(() => {
    if (!backgroundLoading) {
      setShowBackgroundSpinner(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowBackgroundSpinner(true), 200);
    return () => clearTimeout(timer);
  }, [backgroundLoading]);
  const canvasPanRef = React.useRef<PanState | null>(null);
  const pinchRef = React.useRef<{ distance: number; midX: number; midY: number } | null>(null);

  const canvasSize = useSelector(getDefaultCanvasSize);
  const objects = useSelector(getEditorObjects);
  const selectedId = useSelector(getEditorSelectedId);
  const designSize = useSelector(getEditorDesignSize);
  const resolvedBackgroundOption = useSelector(getResolvedBackgroundOption);
  const backgroundDetailImageUrl = useSelector(getResolvedBackgroundDetailImageUrl);
  const fonts = useSelector(getFonts);

  const canvasW = canvasSize.width || DEFAULT_CANVAS_WIDTH_MM;
  const canvasH = canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM;

  // Room scenes are environments the customer wants to see whole → the editor opens
  // (and returns) to the full-scene fit-canvas view. Flat walls / no backdrop keep the
  // classic product view that frames the item.
  const backgroundIsRoom =
    String(
      getItemAttributeValueByIdentifier(resolvedBackgroundOption, AttributeIdentifiers.backgroundType, '') || ''
    ).toLowerCase() === BackgroundTypes.room;

  // Rect the backdrop fills: the fit-to-viewport rect (matches the default view exactly and
  // extends past the canvas to the viewport edges), so the wall covers edge-to-edge with no
  // letterbox stripes while remaining a fixed design-space rect that zooms with the design.
  const backgroundFillRect = React.useMemo(
    () => rectFitCanvas(canvasW, canvasH, viewport),
    [canvasW, canvasH, viewport.width, viewport.height]
  );

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setViewport({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { viewRect, pxPerMm, transform, panByPixels, zoomAtPoint } = useEditorViewport({
    canvasW,
    canvasH,
    objects,
    selectedId,
    designSize,
    viewport,
    backgroundIsRoom,
  });

  const itemBounds = React.useCallback((item: EditorItem) => ({
    x: item.x,
    y: item.y,
    width: item.width,
    height: item.height,
  }), []);

  const combinedBounds = React.useMemo(() => computeDesignSize(objects), [objects]);

  const itemStrokeWidths = useItemStrokeWidths(objects, fonts);
  const surfaceBlurRadius = React.useMemo(
    () => computeFilterBlurRadius(objects, pxPerMm, itemStrokeWidths),
    [objects, pxPerMm, itemStrokeWidths]
  );
  const glossObjects = React.useMemo(
    () => Object.fromEntries(Object.entries(objects).filter(([, item]) => isGlossSurface(item.selectedOptions?.[SURFACE_ID]))),
    [objects]
  );
  const matteObjects = React.useMemo(
    () => Object.fromEntries(Object.entries(objects).filter(([, item]) => isMatteSurface(item.selectedOptions?.[SURFACE_ID]))),
    [objects]
  );
  const neutralObjects = React.useMemo(
    () => Object.fromEntries(Object.entries(objects).filter(([, item]) => !isGlossSurface(item.selectedOptions?.[SURFACE_ID]) && !isMatteSurface(item.selectedOptions?.[SURFACE_ID]))),
    [objects]
  );

  const glossFilterActive = Object.keys(glossObjects).length > 0 && surfaceBlurRadius > 0;
  const matteFilterActive = Object.keys(matteObjects).length > 0 && surfaceBlurRadius > 0;
  // Padded so bevel distortion from alpha being cut at the region boundary
  // stays outside the visible area.
  const surfaceFilterRegion = React.useMemo(() => {
    const pad = surfaceBlurRadius * 6 + Math.max(viewRect.width, viewRect.height) * 0.02;
    return {
      x: viewRect.x - pad,
      y: viewRect.y - pad,
      width: viewRect.width + pad * 2,
      height: viewRect.height + pad * 2,
    };
  }, [viewRect, surfaceBlurRadius]);

  const selectedBounds = React.useMemo(() => {
    if (selectedId && objects[selectedId]) {
      return itemBounds(objects[selectedId]);
    }
    return null;
  }, [selectedId, objects, itemBounds]);

  const measurementBounds = React.useMemo(() => {
    if (selectedBounds) {
      return selectedBounds;
    }
    if (Object.keys(objects).length > 0) {
      return combinedBounds;
    }
    return null;
  }, [selectedBounds, objects, combinedBounds]);

  const objectCount = Object.keys(objects).length;
  const showCombinedBounds = !selectedId && objectCount >= 2;

  const overlayBounds = selectedBounds ?? (showCombinedBounds ? combinedBounds : null);

  const handleSelect = React.useCallback(
    (id: string) => {
      dispatch(setEditorSelectedId(id));
    },
    [dispatch]
  );

  const handleDeselect = React.useCallback(() => {
    dispatch(setEditorSelectedId(null));
  }, [dispatch]);

  const handleDeleteSelected = React.useCallback(
    (id: string) => {
      dispatch(removeEditorItemAndSync(id));
    },
    [dispatch]
  );

  const handleMoveItem = React.useCallback(
    (id: string, x: number, y: number) => {
      dispatch(updateEditorItem(id, { x, y }));
    },
    [dispatch]
  );

  const handleCanvasPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('[data-editor-delete]')) return;
    if (shouldDelegateToEditorItem(e.target)) return;

    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    canvasPanRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
    };
  };

  const handleCanvasPointerMove = (e: React.PointerEvent) => {
    const pan = canvasPanRef.current;
    if (!pan) return;

    const dx = e.clientX - pan.startX;
    const dy = e.clientY - pan.startY;

    if (!pan.moved && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
      pan.moved = true;
    }

    if (pan.moved) {
      panByPixels(e.clientX - pan.lastX, e.clientY - pan.lastY);
      pan.lastX = e.clientX;
      pan.lastY = e.clientY;
    }
  };

  const handleCanvasPointerUp = (e: React.PointerEvent) => {
    const pan = canvasPanRef.current;
    if (!pan) return;

    if ((e.currentTarget as Element).hasPointerCapture(e.pointerId)) {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    }

    if (!pan.moved) {
      handleDeselect();
    }

    canvasPanRef.current = null;
  };

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const svgRect = svgRef.current?.getBoundingClientRect();
      if (!svgRect) return;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      zoomAtPoint(e.clientX, e.clientY, svgRect, factor);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAtPoint]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const distance = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
      pinchRef.current = {
        distance,
        midX: (a.clientX + b.clientX) / 2,
        midY: (a.clientY + b.clientY) / 2,
      };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2 || !pinchRef.current) return;
    e.preventDefault();
    const [a, b] = [e.touches[0], e.touches[1]];
    const distance = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect || pinchRef.current.distance === 0) return;

    const factor = distance / pinchRef.current.distance;
    if (Math.abs(factor - 1) > 0.01) {
      zoomAtPoint(pinchRef.current.midX, pinchRef.current.midY, svgRect, factor);
      pinchRef.current.distance = distance;
    }
  };

  const handleTouchEnd = () => {
    pinchRef.current = null;
  };

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const preventSelect = (e: Event) => {
      e.preventDefault();
    };

    el.addEventListener('selectstart', preventSelect);
    return () => el.removeEventListener('selectstart', preventSelect);
  }, []);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      data-editor-canvas
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <svg
        ref={svgRef}
        className={classes.svg}
        viewBox={`0 0 ${viewport.width || 1} ${viewport.height || 1}`}
        onPointerDownCapture={handleCanvasPointerDown}
        onPointerMoveCapture={handleCanvasPointerMove}
        onPointerUpCapture={handleCanvasPointerUp}
        onPointerCancelCapture={handleCanvasPointerUp}
      >
        <SurfaceGlossFilter
          blurRadius={surfaceBlurRadius}
          region={surfaceFilterRegion}
          designBounds={combinedBounds}
        />
        <SurfaceMatteFilter
          blurRadius={surfaceBlurRadius}
          region={surfaceFilterRegion}
          designBounds={combinedBounds}
        />
        <g transform={transform}>
          <BackgroundLayer
            optionIdentifier={resolvedBackgroundOption?.identifier ?? null}
            detailImageUrl={backgroundDetailImageUrl}
            onLoadingChange={setBackgroundLoading}
            designWidthMm={designSize.width || canvasW}
            fillRect={backgroundFillRect}
          />
          <rect
            x={0}
            y={0}
            width={canvasW}
            height={canvasH}
            fill="transparent"
            pointerEvents="all"
          />
          <DesignItemLayer items={neutralObjects} />
          <g filter={glossFilterActive ? `url(#${SURFACE_GLOSS_FILTER_ID})` : undefined}>
            <g id={SURFACE_GLOSS_ITEMS_ID}>
              <DesignItemLayer items={glossObjects} />
            </g>
          </g>
          <g filter={matteFilterActive ? `url(#${SURFACE_MATTE_FILTER_ID})` : undefined}>
            <g id={SURFACE_MATTE_ITEMS_ID}>
              <DesignItemLayer items={matteObjects} />
            </g>
          </g>
          {glossFilterActive ? (
            <g mask={`url(#${SURFACE_GLOSS_MASK_ID})`} pointerEvents="none">
              <g filter={`url(#${SURFACE_GLOSS_WARP_FILTER_ID})`}>
                <rect
                  x={surfaceFilterRegion.x}
                  y={surfaceFilterRegion.y}
                  width={surfaceFilterRegion.width}
                  height={surfaceFilterRegion.height}
                  fill={`url(#${SURFACE_GLOSS_GRADIENT_ID})`}
                />
                <rect
                  x={surfaceFilterRegion.x}
                  y={surfaceFilterRegion.y}
                  width={surfaceFilterRegion.width}
                  height={surfaceFilterRegion.height}
                  fill={`url(#${SURFACE_GLOSS_STREAK_GRADIENT_ID})`}
                />
              </g>
            </g>
          ) : null}
          {matteFilterActive ? (
            <g mask={`url(#${SURFACE_MATTE_MASK_ID})`} pointerEvents="none">
              <g filter={`url(#${SURFACE_MATTE_WARP_FILTER_ID})`}>
                <rect
                  x={surfaceFilterRegion.x}
                  y={surfaceFilterRegion.y}
                  width={surfaceFilterRegion.width}
                  height={surfaceFilterRegion.height}
                  fill={`url(#${SURFACE_MATTE_GRADIENT_ID})`}
                />
                <rect
                  x={surfaceFilterRegion.x}
                  y={surfaceFilterRegion.y}
                  width={surfaceFilterRegion.width}
                  height={surfaceFilterRegion.height}
                  fill={`url(#${SURFACE_MATTE_STREAK_GRADIENT_ID})`}
                />
              </g>
            </g>
          ) : null}
        </g>
        <DesignItemHitLayer
          items={objects}
          selectedId={selectedId}
          viewRect={viewRect}
          pxPerMm={pxPerMm}
          onSelect={handleSelect}
          onMoveItem={handleMoveItem}
        />
        <ItemMeasurements
          bounds={measurementBounds}
          selectedId={selectedId}
          objectCount={objectCount}
          viewRect={viewRect}
          pxPerMm={pxPerMm}
        />
        <SelectionOverlay
          bounds={overlayBounds}
          selectedId={selectedId}
          viewRect={viewRect}
          pxPerMm={pxPerMm}
          onDelete={handleDeleteSelected}
        />
      </svg>
      {showBackgroundSpinner && (
        <div className={classes.loadingOverlay} aria-hidden>
          <div className={classes.spinner} />
        </div>
      )}
    </div>
  );
}
